import React, {
  useCallback,
  useState,
  useRef,
  useMemo,
  useContext
} from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import SearchIcon from '@material-ui/icons/Search';
import DeleteCircle from '@material-ui/icons/HighlightOffOutlined';
import LocationIcon from '@material-ui/icons/LocationOn';
import Loading from 'react-loading-animation';
import Debounce from 'lodash.debounce';
import MuiInput from '@material-ui/core/Input';
import MenuItem from '@material-ui/core/MenuItem';
import InputAdornment from '@material-ui/core/InputAdornment';
import styled, { ThemeProvider } from 'styled-components';
import CountryContext from 'context/CountryContext';
import {
  onErrorHandler,
  ErrorBoundaryFallbackComponent,
  withErrorBoundary
} from 'components/ErrorBoundry/ErrorBoundry';
import theme from '../../theme/styled-theme';
import { sendGtagEvent } from '../../utils/gtag';
import Suggestion from './Suggestions';

const ACTION_SEARCH_KEYWORD = 'ACTION:SEARCH_KEYWORD';

const Root = styled.div`
  position: relative;
  margin-right: 0 8px;
`;

const Input = styled(MuiInput)`
  && {
    width: 100%;
    padding: 8px 14px;
    border: 1px solid rgb(230, 230, 230);
    box-shadow: rgba(0, 0, 0, 0.05) 0px 2px 13px 0px;
    border-radius: 4px;
  }
`;

const PlaceAutocomplete = props => {
  const {
    searchAutocompletePlace,
    placeHolder,
    suggestion,
    loadingSuggestion,
    selectPlaceId,
    clearSearchResult,
    currentSelection,
    clearSelection,
    searchKeyWordPlace,
    searched,
    name,
    searchResult,
    t
  } = props;
  const country = useContext(CountryContext);
  const { code } = country;
  const debouncedLoadSuggestions = useCallback(
    Debounce(searchAutocompletePlace, 500),
    []
  );
  const [searchKey, setSearchKey] = useState('');
  const [visible, setVisible] = useState(false);
  const _autoComplete = useRef();

  const searchKeyWordOption = useMemo(
    () => (
      <MenuItem
        value={ACTION_SEARCH_KEYWORD}
        leftIcon={<SearchIcon />}
        innerDivStyle={{ paddingLeft: '56px' }}
        onClick={e => {
          e.stopPropagation();
          searchKeyWordPlace(searchKey, code);
        }}
      >
        {`${t('SEARCH_DIALOG.SEARCH_KEYWORD')}: "${searchKey}"`}
      </MenuItem>
    ),
    [searchKey, code]
  );

  const noResult = useMemo(
    () => searched && searchResult && searchResult.length === 0 ? (
      <MenuItem value="0">No search result, please try address.</MenuItem>
    ) : null,
    [searched, searchResult]
  );

  const suggestionToShow = suggestion
    ? suggestion.concat(searchResult)
    : searchResult;

  const onAutocompleteItemSelect = (e, target) => {
    e.stopPropagation();

    if (typeof target !== 'string') {
      sendGtagEvent('Select Item from Autocomplete', 'Search Bar Landing');
      selectPlaceId(target);
      setVisible(false);
    }
  };

  const onClearSearchKeyword = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      clearSelection();
      clearSearchResult(e);

      sendGtagEvent('Clear Autocomplete Text Input', 'Search Bar Landing');

      try {
        _autoComplete.current.focus();
      } catch (err) {
        console.log(err);
      }

      setSearchKey('');
    },
    [_autoComplete]
  );

  const onInputFieldKeypressHandler = useCallback(
    e => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        searchKeyWordPlace(searchKey, code);
      }
    },
    [searchKey, code]
  );

  const onInputChange = useCallback(
    e => {
      const inputLength = e.target.value.length;
      if (inputLength >= 2) {
        clearSearchResult(e);
        debouncedLoadSuggestions(e.target.value, code);
      } else if (inputLength === 0) {
        selectPlaceId({});
      }

      setSearchKey(e.target.value);
      setVisible(true);
    },
    [code]
  );

  const onInputClick = e => {
    e.stopPropagation();
    setVisible(true);
  };

  const getStartAdornment = useMemo(
    () => (
      <InputAdornment position="start">
        <LocationIcon />
      </InputAdornment>
    ),
    []
  );

  const getEndAdornment = useMemo(() => {
    if (loadingSuggestion) {
      return (
        <InputAdornment position="end">
          <Loading width="20px" height="20px" />
        </InputAdornment>
      );
    }

    if (!loadingSuggestion && searchKey) {
      return (
        <InputAdornment
          onClick={onClearSearchKeyword}
          onKeyDown={onClearSearchKeyword}
        >
          <DeleteCircle />
        </InputAdornment>
      );
    }

    return null;
  }, [loadingSuggestion, searchKey]);

  const searchValue = useMemo(
    () => currentSelection.description || currentSelection.name || searchKey,
    [currentSelection, searchKey]
  );

  return (
    <ThemeProvider theme={theme}>
      <Root>
        <Input
          data-cy={`cy-autocomplete-${name}`}
          disableUnderline
          inputRef={_autoComplete}
          placeholder={placeHolder}
          onKeyPress={onInputFieldKeypressHandler}
          onChange={onInputChange}
          onClick={onInputClick}
          value={searchValue}
          startAdornment={getStartAdornment}
          endAdornment={getEndAdornment}
        />
        <Suggestion
          visible={visible}
          searchKey={searchKey}
          searchKeyWordOption={searchKeyWordOption}
          noResult={noResult}
          suggestion={suggestionToShow}
          onAutocompleteItemSelect={onAutocompleteItemSelect}
          onClose={() => setVisible(false)}
        />
      </Root>
    </ThemeProvider>
  );
};

PlaceAutocomplete.propTypes = {
  loadingSuggestion: PropTypes.bool,
  currentSelection: PropTypes.objectOf(PropTypes.any),
  suggestion: PropTypes.arrayOf(PropTypes.any),
  selectPlaceId: PropTypes.func,
  clearSelection: PropTypes.func,
  clearSearchResult: PropTypes.func,
  searchAutocompletePlace: PropTypes.func,
  searchKeyWordPlace: PropTypes.func,
  searched: PropTypes.bool,
  searchResult: PropTypes.arrayOf(PropTypes.any),
  placeHolder: PropTypes.string,
  name: PropTypes.string,
  t: PropTypes.func.isRequired
};

PlaceAutocomplete.defaultProps = {
  loadingSuggestion: false,
  searched: false,
  searchResult: [],
  placeHolder: 'Search Location',
  suggestion: [],
  searchAutocompletePlace: () => {},
  searchKeyWordPlace: () => {},
  selectPlaceId: () => {},
  clearSelection: () => {},
  currentSelection: '',
  clearSearchResult: () => {},
  name: ''
};

export default withErrorBoundary(
  withTranslation(['common'])(PlaceAutocomplete),
  ErrorBoundaryFallbackComponent,
  onErrorHandler
);
